<script setup>
import { ref, computed, watch } from 'vue'
import axios from '../axios.js'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  team: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close', 'update:show'])

const searchQuery = ref('')
const searchResults = ref([])
const searchLoading = ref(false)
const searchError = ref(null)

// Filtered search results
const filteredResults = computed(() => {
  if (!searchQuery.value.trim()) {
    return searchResults.value
  }
  const query = searchQuery.value.toLowerCase()
  return searchResults.value.filter(result => 
    result.team_name?.toLowerCase().includes(query) ||
    result.pb_team_id?.toLowerCase().includes(query) ||
    String(result.id).includes(query)
  )
})

// Search for PB teams
const searchPBTeams = async () => {
  if (!searchQuery.value.trim()) {
    searchResults.value = []
    return
  }

  searchLoading.value = true
  searchError.value = null

  try {
    const response = await axios.get('/api/comparable-data-teams/search', {
      params: { query: searchQuery.value }
    })
    searchResults.value = response.data || []
  } catch (err) {
    searchError.value = 'Failed to search teams'
    console.error('Error searching teams:', err)
    searchResults.value = []
  } finally {
    searchLoading.value = false
  }
}

// Get all PB teams
const getAllPBTeams = async () => {
  searchLoading.value = true
  searchError.value = null

  try {
    const response = await axios.get('/api/comparable-data-teams/all')
    searchResults.value = response.data || []
  } catch (err) {
    searchError.value = 'Failed to load teams'
    console.error('Error loading teams:', err)
    searchResults.value = []
  } finally {
    searchLoading.value = false
  }
}

// Select a PB team mapping
const selectPBTeam = async (pbTeam) => {
  if (!props.team) return

  try {
    // Update the team with the selected PB team ID
    await axios.put(`/api/team-data-management/teams/${props.team.id}`, {
      pb_team_id: pbTeam.pb_team_id || pbTeam.id
    })
    
    // Emit event to refresh parent
    emit('team-updated')
    closeModal()
  } catch (err) {
    searchError.value = 'Failed to update team mapping'
    console.error('Error updating team:', err)
  }
}

// Close modal
const closeModal = () => {
  emit('update:show', false)
  emit('close')
  searchQuery.value = ''
  searchResults.value = []
  searchError.value = null
}

// Watch for search query changes (debounced)
let searchTimeout = null
watch(searchQuery, () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  searchTimeout = setTimeout(() => {
    if (searchQuery.value.trim()) {
      searchPBTeams()
    } else {
      searchResults.value = []
    }
  }, 300)
})

// Watch for modal opening
watch(() => props.show, (isOpen) => {
  if (isOpen && props.team) {
    // Optionally load all teams or wait for search
    // getAllPBTeams()
  }
})
</script>

<template>
  <div 
    v-if="show && team" 
    class="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4"
    data-no-global-context-menu
  >
    <div class="absolute inset-0" @click="closeModal"></div>
    <div class="relative z-10 bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <div class="flex items-center space-x-3">
          <i class="fas fa-link text-pink-500 text-xl"></i>
          <div>
            <div class="text-lg font-semibold text-gray-800">Team Mapping</div>
            <div class="text-xs text-gray-500">Map PB Team and RGM Team IDs</div>
          </div>
        </div>
        <button 
          class="p-2 rounded-full hover:bg-gray-100 text-gray-500" 
          @click="closeModal"
          title="Close"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Team Info Row -->
      <div class="px-6 py-3 border-b border-gray-200 flex-shrink-0">
        <div class="flex items-center gap-4 text-sm">
          <span class="font-medium text-gray-700">Team ID:</span>
          <span class="text-gray-900">{{ team.id }}</span>
          <span v-if="team.pb_team_id" class="ml-auto text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
            PB: {{ team.pb_team_id }}
          </span>
          <span v-if="team.rgm_team_id" class="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
            RGM: {{ team.rgm_team_id }}
          </span>
        </div>
      </div>

      <!-- Search Section -->
      <div class="px-6 py-4 border-b border-gray-200 flex-shrink-0">
        <div class="flex items-center gap-3">
          <div class="flex-1 relative">
            <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            <input 
              v-model="searchQuery"
              type="text"
              class="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:border-pink-500"
              placeholder="Search PB teams by name or ID..."
            />
          </div>
          <button
            @click="getAllPBTeams"
            class="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <i class="fas fa-list"></i>
            Show All
          </button>
        </div>
      </div>

      <!-- Search Results -->
      <div class="flex-1 overflow-auto px-6 py-4">
        <div v-if="searchLoading" class="text-center py-8">
          <i class="fas fa-spinner fa-spin text-2xl text-gray-400 mb-2"></i>
          <p class="text-sm text-gray-500">Searching...</p>
        </div>
        
        <div v-else-if="searchError" class="text-center py-8">
          <i class="fas fa-exclamation-circle text-2xl text-red-400 mb-2"></i>
          <p class="text-sm text-red-600">{{ searchError }}</p>
        </div>

        <div v-else-if="!searchQuery.trim() && searchResults.length === 0" class="text-center py-8">
          <i class="fas fa-search text-4xl text-gray-300 mb-2"></i>
          <p class="text-sm text-gray-500">Enter a search term to find PB teams</p>
        </div>

        <div v-else-if="!filteredResults.length" class="text-center py-8">
          <i class="fas fa-inbox text-4xl text-gray-300 mb-2"></i>
          <p class="text-sm text-gray-500">No teams found matching your search</p>
        </div>

        <div v-else class="space-y-2">
          <div class="text-xs text-gray-500 mb-3">
            Found {{ filteredResults.length }} team{{ filteredResults.length !== 1 ? 's' : '' }}
          </div>
          <div class="space-y-2">
            <div
              v-for="result in filteredResults"
              :key="result.id"
              class="bg-white border border-gray-200 rounded-lg p-4 hover:border-pink-300 hover:shadow-md transition-all cursor-pointer"
              @click="selectPBTeam(result)"
            >
              <div class="flex items-center justify-between">
                <div class="flex-1">
                  <div class="flex items-center gap-3 mb-2">
                    <span class="text-sm font-medium text-gray-900">{{ result.team_name || 'Unknown Team' }}</span>
                    <span class="text-xs text-gray-500">ID: {{ result.id }}</span>
                  </div>
                  <div v-if="result.pb_team_id" class="text-xs text-gray-600">
                    <span class="font-medium">PB Team ID:</span> {{ result.pb_team_id }}
                  </div>
                </div>
                <button
                  class="px-3 py-1.5 text-xs bg-pink-500 text-white rounded hover:bg-pink-600 transition-colors flex items-center gap-2"
                  @click.stop="selectPBTeam(result)"
                >
                  <i class="fas fa-link"></i>
                  Map
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between flex-shrink-0">
        <div class="text-xs text-gray-500">
          Select a team to map PB Team ID
        </div>
        <button
          @click="closeModal"
          class="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100"
        >
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>
